import React, { useEffect, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import apiService from "../../../service/Api";
import EmailTemplateLayout from "./components/EmailTemplateLayout";
import EmailTemplateStepper from "./components/EmailTemplateStepper";
import EmailPreviewSidebar from "./components/EmailPreviewSidebar";
import TemplateInfoStep from "./components/steps/TemplateInfoStep";
import ContentStep from "./components/steps/ContentStep";
import MediaStep from "./components/steps/MediaStep";
import DesignStep from "./components/steps/DesignStep";
import { EmailTemplateStepProps } from "./types/types";
import { EmailTemplateData, initialEmailTemplateValues } from "./types/email-templates";

const STEPS: { label: string; component: React.ComponentType<EmailTemplateStepProps> }[] = [
  { label: "Template Info", component: TemplateInfoStep },
  { label: "Content", component: ContentStep },
  { label: "Media", component: MediaStep },
  { label: "Design", component: DesignStep },
];

interface ApiEmailTemplate {
  id: number;
  name: string;
  description: string;
  category: string;
  email_type: string;
  trigger: string;
  subject: string;
  heading: string;
  message: string;
  button_label: string;
  button_link: string;
  logo_url: string | null;
  banner_url: string | null;
  footer_message: string;
  contact_email: string;
  contact_phone: string;
  design: string;
}

const toApiPayload = (data: EmailTemplateData) => ({
  name: data.template_name,
  description: data.description,
  category: data.category,
  email_type: data.email_type,
  trigger: data.trigger,
  subject: data.subject,
  heading: data.heading,
  message: data.email_message,
  button_label: data.button_label,
  button_link: data.button_url,
  logo_url: data.logo_url || null,
  banner_url: data.banner_url || null,
  footer_message: data.footer_message,
  contact_email: data.contact_email,
  contact_phone: data.contact_phone,
  design: data.design_id || "classic",
});

const fromApiTemplate = (t: ApiEmailTemplate): EmailTemplateData => ({
  template_name: t.name,
  description: t.description,
  category: t.category,
  email_type: t.email_type,
  trigger: t.trigger,
  subject: t.subject,
  heading: t.heading,
  email_message: t.message,
  button_label: t.button_label,
  button_url: t.button_link,
  logo_url: t.logo_url || "",
  banner_url: t.banner_url || "",
  footer_message: t.footer_message,
  contact_email: t.contact_email,
  contact_phone: t.contact_phone,
  design_id: t.design,
});

interface AddEmailTemplateProps {
  isEdit?: boolean;
  templateId?: string;
}

const AddEmailTemplate: React.FC<AddEmailTemplateProps> = ({ isEdit = false, templateId }) => {
  const navigate = useNavigate();

  const [activeStep, setActiveStep] = useState(0);
  const [templateData, setTemplateData] = useState(initialEmailTemplateValues);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(isEdit);

  useEffect(() => {
    if (!isEdit || !templateId) return;
    setLoading(true);
    apiService
      .get<ApiEmailTemplate>(`/api/configurations/email-templates/${templateId}/`)
      .then((res) => setTemplateData(fromApiTemplate(res.data)))
      .catch(() => toast.error("Failed to load the email template."))
      .finally(() => setLoading(false));
  }, [isEdit, templateId]);

  const isFirstStep = activeStep === 0;
  const isLastStep = activeStep === STEPS.length - 1;
  const ActiveComponent = STEPS[activeStep]?.component;

  const validateStep = (): boolean => {
    if (activeStep === 0 && !templateData.template_name.trim()) {
      toast.error("Template name is required");
      return false;
    }
    if (activeStep === 1 && (!templateData.subject.trim() || !templateData.email_message.trim())) {
      toast.error("Subject and Email Message are required");
      return false;
    }
    return true;
  };

  const saveTemplate = async () => {
    if (!templateData.design_id) {
      toast.error("Please select a design before saving");
      return;
    }
    setSubmitting(true);
    try {
      const payload = toApiPayload(templateData);
      const response =
        isEdit && templateId
          ? await apiService.patch<ApiEmailTemplate>(
              `/api/configurations/email-templates/${templateId}/`,
              payload,
            )
          : await apiService.post<ApiEmailTemplate>(
              "/api/configurations/email-templates/",
              payload,
            );

      toast.success(`Template ${isEdit ? "updated" : "created"} successfully`, {
        position: "top-right",
        autoClose: 2000,
        theme: "light",
      });
      setTimeout(
        () => navigate(`/configurations/email-templates/view/${response.data.id}`),
        1000,
      );
    } catch {
      toast.error("Failed to save the email template.", {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleNext = () => {
    if (!validateStep()) return;
    if (isLastStep) saveTemplate();
    else setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    if (activeStep > 0) setActiveStep((prev) => prev - 1);
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <EmailTemplateLayout
      onNext={handleNext}
      onBack={handleBack}
      isFirstStep={isFirstStep}
      isLastStep={isLastStep}
      submitting={submitting}
      isEdit={isEdit}
      submitLabel="Save"
      submittingLabel="Saving..."
      sidebarTitle="Template Information"
      stepperComponent={
        <EmailTemplateStepper
          steps={STEPS.map((s) => s.label)}
          activeStep={activeStep}
          onStepClick={setActiveStep}
        />
      }
      formContentComponent={
        ActiveComponent ? (
          <ActiveComponent
            templateData={templateData}
            setTemplateData={setTemplateData}
            onNext={handleNext}
            onBack={handleBack}
            submitting={submitting}
          />
        ) : null
      }
      sidebarContentComponent={<EmailPreviewSidebar templateData={templateData} />}
    />
  );
};

export default AddEmailTemplate;